import { useCallback, useEffect, useState } from 'react';
import { UploadError } from '@ff-os/shared-react/components/file-uploader/FileUploader';
import { useDebounce } from '@ff-os/shared-react/hooks/useDebounce';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';

type Options = {
    since: number;
    initialUploadErrors?: UploadError[];
    onError?: (errors: UploadError[]) => void;
};

export const useNewFailedUploadsCount = (
    photos: PhotoPrintsJson.Photo[],
    { since, onError, initialUploadErrors }: Options
) => {

    const [count, setCount] = useState(initialUploadErrors?.length ?? 0);
    const [failedIds, setFailedIds] = useState<string[]>(photos
        .filter(p => p.status === 'error')
        .map(p => p.id));

    const reset = useCallback(() => {
        setCount(0);
    }, []);

    const debouncedReset = useDebounce(reset, since);

    const handleFileInputError = useCallback((errors: UploadError[]) => {
        onError && onError(errors);
        debouncedReset();
        setCount(count => count + errors.filter(e => e.type === 'file').length);
    }, [debouncedReset, onError]);

    useEffect(() => {

        const newFailedIds = photos
            .filter(p => p.status === 'error')
            .filter(p => !failedIds.includes(p.id))
            .map(p => p.id);

        debouncedReset();
        setCount(count => count + newFailedIds.length);

        if (newFailedIds.length > 0) {
            setFailedIds([...failedIds, ...newFailedIds]);
        }
    }, [photos, failedIds, debouncedReset]);

    return { failedUploadsCount: count, handleFileInputError };
};